import { ApolloClient } from '@apollo/client'

import {
  MaterialBookingDocument,
  MyBookingDocument,
} from '@/graphql/api/generated'

/**
 * Extracts the materialBooking ID from an IRI (e.g., "/material_bookings/123" -> "/material_bookings/123")
 * or returns null if the IRI is not a materialBooking IRI.
 */
function extractMaterialBookingId(
  iri: string | undefined | null
): string | null {
  if (!iri || typeof iri !== 'string') return null
  const trimmed = iri.trim()
  if (!trimmed.includes('/material_bookings/')) return null
  return trimmed
}

/**
 * Refetches materialBooking queries when navigating from a notification.
 * This ensures the booking data is up-to-date when the user clicks on a notification link.
 *
 * @param client - Apollo Client instance
 * @param bookingId - MaterialBooking IRI (e.g., "/material_bookings/123") or context object with bookingId
 */
export async function refetchMaterialBookingQueries(
  client: ApolloClient,
  bookingId: string | { bookingId?: string } | undefined | null
): Promise<void> {
  let materialBookingId: string | null = null

  if (typeof bookingId === 'string') {
    materialBookingId = extractMaterialBookingId(bookingId)
  } else if (
    bookingId &&
    typeof bookingId === 'object' &&
    'bookingId' in bookingId
  ) {
    materialBookingId = extractMaterialBookingId(bookingId.bookingId)
  }

  if (!materialBookingId) return

  try {
    // Refetch both queries to ensure all views are updated
    // Using fetchPolicy: 'network-only' forces a network request and updates the cache
    await Promise.all([
      client.query({
        query: MaterialBookingDocument,
        variables: { id: materialBookingId },
        fetchPolicy: 'network-only',
      }),
      client.query({
        query: MyBookingDocument,
        variables: { id: materialBookingId },
        fetchPolicy: 'network-only',
      }),
    ])
  } catch (error) {
    // Silently fail - the queries will be refetched on next render anyway
    console.warn('[refetchMaterialBookingQueries] Failed to refetch:', error)
  }
}
